// =============================================================================
// FORM FIELD CARD - Professional sortable field in the canvas like SurveyJS
// =============================================================================

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  Trash2,
  Copy,
  Settings,
  Type,
  Mail,
  Hash,
  Phone,
  AlignLeft,
  ChevronDown,
  ListChecks,
  CheckSquare,
  CircleDot,
  Calendar,
  Clock,
  CalendarClock,
  Upload,
  ImagePlus,
  Image,
  PenTool,
  Star,
  Gauge,
  SlidersHorizontal,
  ListOrdered,
  Grid3X3,
  Heading,
  Text,
  Code,
  LayoutTemplate,
  PanelTop,
  Minus,
  Link,
  Lock,
  ToggleLeft,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Slider } from '../../../components/ui/slider';
import { Switch } from '../../../components/ui/switch';
import type { FormField } from '../types';
import { useFormBuilderStore } from '../store';

const ICON_MAP: Record<string, LucideIcon> = {
  text: Type,
  email: Mail,
  number: Hash,
  phone: Phone,
  url: Link,
  password: Lock,
  textarea: AlignLeft,
  select: ChevronDown,
  multiselect: ListChecks,
  checkbox: CheckSquare,
  checkboxgroup: CheckSquare,
  radio: CircleDot,
  boolean: ToggleLeft,
  date: Calendar,
  time: Clock,
  datetime: CalendarClock,
  file: Upload,
  image: ImagePlus,
  signature: PenTool,
  rating: Star,
  nps: Gauge,
  slider: SlidersHorizontal,
  ranking: ListOrdered,
  matrix: Grid3X3,
  heading: Heading,
  paragraph: Text,
  html: Code,
  'image-display': Image,
  section: LayoutTemplate,
  panel: PanelTop,
  divider: Minus,
};

interface FormFieldCardProps {
  field: FormField;
  sectionId: string;
  isSelected: boolean;
  onClick: (e: React.MouseEvent) => void;
}

export function FormFieldCard({ field, sectionId, isSelected, onClick }: FormFieldCardProps) {
  const { removeField, duplicateField, selectField } = useFormBuilderStore();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const Icon = ICON_MAP[field.type] || Type;
  const isRequired = field.validation.some((v) => v.type === 'required');
  const hasConditions = (field.conditions?.length || 0) > 0;

  // Get field type label in French
  const getFieldTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      text: 'Texte',
      email: 'Email',
      number: 'Nombre',
      phone: 'Téléphone',
      url: 'URL',
      password: 'Mot de passe',
      textarea: 'Texte long',
      select: 'Liste',
      multiselect: 'Multi-sélection',
      checkbox: 'Case à cocher',
      checkboxgroup: 'Groupe de cases',
      radio: 'Boutons radio',
      boolean: 'Oui/Non',
      date: 'Date',
      time: 'Heure',
      datetime: 'Date et heure',
      file: 'Fichier',
      image: 'Image',
      signature: 'Signature',
      rating: 'Étoiles',
      nps: 'NPS',
      slider: 'Curseur',
      ranking: 'Classement',
      matrix: 'Matrice',
      heading: 'Titre',
      paragraph: 'Paragraphe',
      html: 'HTML',
      'image-display': 'Afficher image',
      section: 'Section',
      panel: 'Panneau',
      divider: 'Séparateur',
    };
    return labels[type] || type;
  };

  // Render field preview based on type
  const renderFieldPreview = () => {
    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
      case 'url':
        return (
          <Input
            placeholder={field.placeholder || 'Saisissez...'}
            disabled
            className="bg-secondary-50 dark:bg-secondary-900 cursor-default"
          />
        );

      case 'password':
        return (
          <Input
            type="password"
            placeholder={field.placeholder || '••••••••'}
            disabled
            className="bg-secondary-50 dark:bg-secondary-900 cursor-default"
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            placeholder={field.placeholder || '0'}
            disabled
            className="bg-secondary-50 dark:bg-secondary-900 cursor-default"
          />
        );

      case 'textarea':
        return (
          <Textarea
            placeholder={field.placeholder || 'Saisissez votre texte...'}
            disabled
            rows={field.rows || 3}
            className="bg-secondary-50 dark:bg-secondary-900 cursor-default resize-none"
          />
        );

      case 'select':
        return (
          <div className="h-10 rounded-md border border-secondary-200 bg-secondary-50 px-3 flex items-center justify-between dark:border-secondary-700 dark:bg-secondary-900">
            <span className="text-secondary-500">{field.placeholder || 'Sélectionnez...'}</span>
            <ChevronDown className="h-4 w-4 text-secondary-400" />
          </div>
        );

      case 'multiselect':
        return (
          <div className="min-h-[40px] rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2 flex items-center gap-2 flex-wrap dark:border-secondary-700 dark:bg-secondary-900">
            {field.options?.slice(0, 2).map((opt, i) => (
              <span
                key={i}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
              >
                {opt.label}
              </span>
            ))}
            {(field.options?.length || 0) > 2 && (
              <span className="text-xs text-secondary-500">+{(field.options?.length || 0) - 2}</span>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div className="flex items-center gap-3">
            <div className="h-5 w-5 rounded border-2 border-secondary-300 dark:border-secondary-600" />
            <span className="text-sm text-secondary-700 dark:text-secondary-300">
              {field.description || 'Case à cocher'}
            </span>
          </div>
        );

      case 'checkboxgroup':
        return (
          <div className="space-y-2">
            {field.options?.slice(0, 3).map((option, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="h-4 w-4 rounded border border-secondary-300 dark:border-secondary-600" />
                <span className="text-sm text-secondary-600 dark:text-secondary-400">
                  {option.label}
                </span>
              </div>
            ))}
            {(field.options?.length || 0) > 3 && (
              <p className="text-xs text-secondary-500 pl-7">
                +{(field.options?.length || 0) - 3} autres options
              </p>
            )}
          </div>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {field.options?.slice(0, 3).map((option, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="h-4 w-4 rounded-full border-2 border-secondary-300 dark:border-secondary-600" />
                <span className="text-sm text-secondary-600 dark:text-secondary-400">
                  {option.label}
                </span>
              </div>
            ))}
            {(field.options?.length || 0) > 3 && (
              <p className="text-xs text-secondary-500 pl-7">
                +{(field.options?.length || 0) - 3} autres options
              </p>
            )}
          </div>
        );

      case 'boolean':
        return (
          <div className="flex items-center gap-3">
            <Switch disabled />
            <span className="text-sm text-secondary-600 dark:text-secondary-400">
              {field.description || 'Oui / Non'}
            </span>
          </div>
        );

      case 'date':
        return (
          <div className="h-10 rounded-md border border-secondary-200 bg-secondary-50 px-3 flex items-center justify-between dark:border-secondary-700 dark:bg-secondary-900">
            <span className="text-secondary-500">jj/mm/aaaa</span>
            <Calendar className="h-4 w-4 text-secondary-400" />
          </div>
        );

      case 'time':
        return (
          <div className="h-10 rounded-md border border-secondary-200 bg-secondary-50 px-3 flex items-center justify-between dark:border-secondary-700 dark:bg-secondary-900">
            <span className="text-secondary-500">hh:mm</span>
            <Clock className="h-4 w-4 text-secondary-400" />
          </div>
        );

      case 'datetime':
        return (
          <div className="h-10 rounded-md border border-secondary-200 bg-secondary-50 px-3 flex items-center justify-between dark:border-secondary-700 dark:bg-secondary-900">
            <span className="text-secondary-500">jj/mm/aaaa hh:mm</span>
            <CalendarClock className="h-4 w-4 text-secondary-400" />
          </div>
        );

      case 'file':
        return (
          <div className="h-24 rounded-lg border-2 border-dashed border-secondary-300 bg-secondary-50 flex items-center justify-center dark:border-secondary-600 dark:bg-secondary-900">
            <div className="text-center">
              <Upload className="h-8 w-8 text-secondary-400 mx-auto mb-1" />
              <span className="text-sm text-secondary-500">
                Cliquez ou glissez vos fichiers
              </span>
              {field.maxFiles && (
                <p className="text-xs text-secondary-400 mt-1">
                  Max {field.maxFiles} fichier(s)
                </p>
              )}
            </div>
          </div>
        );

      case 'image':
        return (
          <div className="h-32 rounded-lg border-2 border-dashed border-secondary-300 bg-secondary-50 flex items-center justify-center dark:border-secondary-600 dark:bg-secondary-900">
            <div className="text-center">
              <ImagePlus className="h-10 w-10 text-secondary-400 mx-auto mb-1" />
              <span className="text-sm text-secondary-500">
                Cliquez pour ajouter une image
              </span>
            </div>
          </div>
        );

      case 'signature':
        return (
          <div className="h-28 rounded-lg border-2 border-dashed border-secondary-300 bg-white flex items-center justify-center dark:border-secondary-600 dark:bg-secondary-900">
            <div className="text-center">
              <PenTool className="h-8 w-8 text-secondary-400 mx-auto mb-1" />
              <span className="text-sm text-secondary-500">
                Cliquez pour signer
              </span>
            </div>
          </div>
        );

      case 'rating':
        return (
          <div className="flex gap-1">
            {Array.from({ length: field.ratingCount || 5 }).map((_, i) => (
              <Star
                key={i}
                className={cn(
                  'h-7 w-7 transition-colors cursor-pointer',
                  i < 3
                    ? 'fill-amber-400 text-amber-400'
                    : 'text-secondary-300 dark:text-secondary-600'
                )}
              />
            ))}
          </div>
        );

      case 'nps':
        return (
          <div className="space-y-2">
            <div className="flex gap-1">
              {Array.from({ length: 11 }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={cn(
                    'flex-1 h-9 rounded text-sm font-medium transition-colors',
                    i <= 6
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      : i <= 8
                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                      : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                    i === 8 && 'ring-2 ring-primary-500'
                  )}
                >
                  {i}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-xs text-secondary-500">
              <span>Pas du tout probable</span>
              <span>Très probable</span>
            </div>
          </div>
        );

      case 'slider':
        return (
          <div className="space-y-3 pt-2">
            <Slider
              defaultValue={[50]}
              min={field.minValue ?? 0}
              max={field.maxValue ?? 100}
              step={field.step ?? 1}
              disabled
            />
            <div className="flex justify-between text-xs text-secondary-500">
              <span>{field.minValue ?? 0}</span>
              {field.showValue && (
                <span className="font-medium text-primary-600">50</span>
              )}
              <span>{field.maxValue ?? 100}</span>
            </div>
          </div>
        );

      case 'ranking':
        return (
          <div className="space-y-1">
            {field.options?.slice(0, 3).map((option, i) => (
              <div
                key={i}
                className="flex items-center gap-3 p-2 rounded-lg bg-secondary-100 dark:bg-secondary-700"
              >
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-500 text-white text-xs font-bold">
                  {i + 1}
                </span>
                <GripVertical className="h-4 w-4 text-secondary-400" />
                <span className="text-sm text-secondary-700 dark:text-secondary-300">
                  {option.label}
                </span>
              </div>
            ))}
          </div>
        );

      case 'matrix':
        return (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="p-2 text-left"></th>
                  {field.matrixColumns?.map((col) => (
                    <th
                      key={col.value}
                      className="p-2 text-center text-secondary-600 dark:text-secondary-400 font-medium"
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {field.matrixRows?.slice(0, 2).map((row) => (
                  <tr key={row.value} className="border-t border-secondary-200 dark:border-secondary-700">
                    <td className="p-2 text-secondary-700 dark:text-secondary-300">
                      {row.label}
                    </td>
                    {field.matrixColumns?.map((col) => (
                      <td key={col.value} className="p-2 text-center">
                        <div className="h-4 w-4 rounded-full border-2 border-secondary-300 dark:border-secondary-600 mx-auto" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case 'heading':
        const HeadingTag = field.headingLevel || 'h2';
        const headingSizes: Record<string, string> = {
          h1: 'text-2xl',
          h2: 'text-xl',
          h3: 'text-lg',
          h4: 'text-base',
        };
        return (
          <HeadingTag
            className={cn(
              'font-bold text-secondary-900 dark:text-white',
              headingSizes[HeadingTag]
            )}
          >
            {field.label}
          </HeadingTag>
        );

      case 'paragraph':
        return (
          <p className="text-secondary-600 dark:text-secondary-400">
            {field.description || 'Texte descriptif pour votre formulaire.'}
          </p>
        );

      case 'html':
        return (
          <div
            className="p-3 rounded-lg bg-secondary-100 dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700"
            dangerouslySetInnerHTML={{
              __html: field.htmlContent || '<p>Contenu HTML personnalisé</p>',
            }}
          />
        );

      case 'image-display':
        return (
          <div className="h-32 rounded-lg bg-secondary-100 dark:bg-secondary-800 flex items-center justify-center">
            <Image className="h-16 w-16 text-secondary-400" />
          </div>
        );

      case 'divider':
        return <div className="h-px bg-secondary-300 dark:bg-secondary-600 my-2" />;

      case 'panel':
        return (
          <div className="rounded-lg border border-secondary-200 dark:border-secondary-700 p-3 bg-secondary-50 dark:bg-secondary-800">
            <div className="flex items-center gap-2 mb-2">
              <PanelTop className="h-4 w-4 text-secondary-500" />
              <span className="font-medium text-secondary-700 dark:text-secondary-300">
                {field.label}
              </span>
            </div>
            <p className="text-xs text-secondary-500">
              Les champs enfants seront affichés ici
            </p>
          </div>
        );

      default:
        return (
          <div className="p-3 rounded-lg bg-secondary-100 dark:bg-secondary-800 text-center">
            <span className="text-sm text-secondary-500">
              Aperçu non disponible
            </span>
          </div>
        );
    }
  };

  // Layout/display fields have simplified rendering
  if (['divider', 'heading', 'paragraph', 'html', 'image-display'].includes(field.type)) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={cn(
          'group rounded-lg border p-4 transition-all cursor-pointer',
          isSelected
            ? 'border-primary-500 ring-2 ring-primary-500/20 bg-primary-50/30 dark:bg-primary-900/10'
            : 'border-secondary-200 bg-white hover:border-secondary-300 dark:border-secondary-700 dark:bg-secondary-800 dark:hover:border-secondary-600',
          isDragging && 'opacity-50 shadow-xl'
        )}
        onClick={onClick}
      >
        <div className="flex items-center gap-2 mb-2">
          <button
            type="button"
            className="cursor-grab touch-none opacity-0 group-hover:opacity-100 transition-opacity"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4 text-secondary-400" />
          </button>
          <Badge variant="secondary" className="text-[10px]">
            {getFieldTypeLabel(field.type)}
          </Badge>
          <div className="flex-1" />
          {isSelected && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={(e) => {
                  e.stopPropagation();
                  duplicateField(sectionId, field.id);
                }}
              >
                <Copy className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50"
                onClick={(e) => {
                  e.stopPropagation();
                  removeField(sectionId, field.id);
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>
        <div>{renderFieldPreview()}</div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group rounded-lg border bg-white transition-all cursor-pointer',
        'dark:bg-secondary-800',
        isSelected
          ? 'border-primary-500 ring-2 ring-primary-500/20 shadow-lg'
          : 'border-secondary-200 hover:border-secondary-300 hover:shadow-md dark:border-secondary-700 dark:hover:border-secondary-600',
        isDragging && 'opacity-50 shadow-xl scale-[1.02]'
      )}
      onClick={onClick}
    >
      {/* Header toolbar */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-secondary-100 dark:border-secondary-700">
        <button
          type="button"
          className="cursor-grab touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-secondary-400 hover:text-secondary-600" />
        </button>

        <Icon className="h-4 w-4 text-secondary-500" />

        <span className="font-medium text-secondary-900 dark:text-white text-sm truncate">
          {field.label}
        </span>

        {isRequired && (
          <span className="text-red-500 font-bold">*</span>
        )}

        {hasConditions && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800">
            Conditionnel
          </Badge>
        )}

        <div className="flex-1" />

        <Badge variant="secondary" className="text-[10px] opacity-60">
          {getFieldTypeLabel(field.type)}
        </Badge>

        {/* Action buttons - visible on hover or when selected */}
        <div className={cn(
          'flex items-center gap-1 transition-opacity',
          isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        )}>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => {
              e.stopPropagation();
              selectField(field.id);
            }}
          >
            <Settings className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => {
              e.stopPropagation();
              duplicateField(sectionId, field.id);
            }}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
            onClick={(e) => {
              e.stopPropagation();
              removeField(sectionId, field.id);
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Field preview */}
      <div className="p-4">
        {renderFieldPreview()}
      </div>

      {/* Help text */}
      {field.helpText && (
        <div className="px-4 pb-3">
          <p className="text-xs text-secondary-500 dark:text-secondary-400">
            {field.helpText}
          </p>
        </div>
      )}
    </div>
  );
}
